// @flow strict

import { type Obj } from '@mondough/public-web-components'
import { renderTestSection } from '../../components/bespoke/TestSection'
import { renderCardRender } from './cardRender'
import {
  renderPlusBundles,
  renderPlusFeatures,
  renderPlusInsurance,
  renderPaidFeatureDiscoveryGrid,
  renderCustomTextAndImageWithBackground,
} from './bespokePaidComponents'
import { renderEventsSpeakersList } from './events'
import { renderOverdraftFeeCalculator } from '../../components/bespoke/OverdraftFeeCalculator/OverdraftFeeCalculator'
import { renderOverdraftEligibilityChecker } from '../../components/bespoke/OverdraftEligibilityChecker/OverdraftEligibilitySection'
import { renderTrustMarkerStrip } from '../../components/bespoke/TrustMarkerStrip/TrustMarkerStrip'
import {
  renderUSAFeatureChecklist,
  renderUSAWaitlist,
} from './bespokeUSAComponents'
import {
  renderBusinessMoreInformationSection,
  renderBusinessCustomerQuoteDiptych,
  renderBusinessCustomerProfileTriptych,
  renderBusinessFeatureDiscoveryGrid,
  renderBusinessComparisonTable,
  renderBusinessPricingTable,
} from './bespokeBusinessComponents'
import { renderECBRates } from './ECBRates'
import { renderBusinesssBankingCTABanner } from './businessBankingCTABanner'

// How to register a bespoke section to the feature page CMS front-end

// 1. Add your section name to the enum types. Make it descriptive.
// This should match the name on the Contentful list!
type BespokeSectionNames =
  | 'Test Bespoke Section'
  | 'Events - Speaker list'
  | 'Overdraft Fee Calculator'
  | 'Overdraft Eligibility Checker'
  | 'Paid Monzo - Both - Compare Bundles'
  | 'Paid Monzo - Plus - Card Render With CTA'
  | 'Paid Monzo - Plus - Features Grid'
  | 'Paid Monzo - Premium - Card Render With CTA'
  | 'Paid Monzo - Premium - Features Grid'
  | 'Paid Monzo - Premium - Insurance'
  | 'USA - Features checklist'
  | 'USA - Join Waitlist Form'
  | 'Business - More Information Form'
  | 'Business - Pricing Table'
  | 'Business - Customer Quote Diptych - Testimonials Page'
  | 'Business - Customer Quote Diptych - Features Page'
  | 'Business - Customer Profile Triptych - Landing Page'
  | 'Business - Customer Profile Triptych - Testimonials Page 1'
  | 'Business - Customer Profile Triptych - Testimonials Page 2'
  | 'Business - Comparison Table'
  | 'Business - Trust Marker strip'
  | 'Business - Feature Discovery Grid'
  | 'Paid - Feature Discovery Grid'
  | 'Insurance - Feature Discovery Grid'
  | 'European Central Bank Rates'
  | 'Paid Monzo - Plus - Custom Categories with Background'
  | 'Paid Monzo - Plus - Everything you love about Monzo with Background'
  | 'Paid Monzo - Premium - Other accounts with Background'
  | 'Business Banking Link Out Banner'
  | 'Paid Monzo - All - Compare Bundles'
  | 'Personal banking - Trustmarks'

// 2. Give your section name a short code to use within the code. This name should match the list above.
const bespokeCodes: Obj<string, BespokeSectionNames> = {
  TEST: 'Test Bespoke Section',
  EVENTS_SPEAKER_LIST: 'Events - Speaker list',
  OVERDRAFT_FEE_CALC: 'Overdraft Fee Calculator',
  OVERDRAFT_ELIGIBLE: 'Overdraft Eligibility Checker',
  PAID_BUNDLES: 'Paid Monzo - Both - Compare Bundles',
  ALL_BUNDLES: 'Paid Monzo - All - Compare Bundles',
  PREMIUM_FEATURES: 'Paid Monzo - Premium - Features Grid',
  PLUS_FEATURES: 'Paid Monzo - Plus - Features Grid',
  PLUS_INSURANCE: 'Paid Monzo - Premium - Insurance',
  CARD_RENDER_CTA_PREMIUM: 'Paid Monzo - Premium - Card Render With CTA',
  CARD_RENDER_CTA_PLUS: 'Paid Monzo - Plus - Card Render With CTA',
  USA_FEATURES: 'USA - Features checklist',
  USA_WAITLIST: 'USA - Join Waitlist Form',
  BUSINESS_MORE_INFORMATION_FORM: 'Business - More Information Form',
  BUSINESS_PRICING_TABLE: 'Business - Pricing Table',
  BUSINESS_CUSTOMER_QUOTE_DIPTYCH_TESTIMONIALS:
    'Business - Customer Quote Diptych - Testimonials Page',
  BUSINESS_CUSTOMER_QUOTE_DIPTYCH_FEATURES:
    'Business - Customer Quote Diptych - Features Page',
  BUSINESS_CUSTOMER_PROFILE_TRIPTYCH_LANDING:
    'Business - Customer Profile Triptych - Landing Page',
  BUSINESS_CUSTOMER_PROFILE_TRIPTYCH_TESTIMONIALS_1:
    'Business - Customer Profile Triptych - Testimonials Page 1',
  BUSINESS_CUSTOMER_PROFILE_TRIPTYCH_TESTIMONIALS_2:
    'Business - Customer Profile Triptych - Testimonials Page 2',
  BUSINESS_COMPARISON_TABLE: 'Business - Comparison Table',
  BUSINESS_TRUST_MARKER_STRIP: 'Business - Trust Marker strip',
  BUSINESS_FEATURE_DISCOVERY: 'Business - Feature Discovery Grid',
  PAID_FEATURE_DISCOVERY: 'Paid - Feature Discovery Grid',
  PAID_INSURANCE_FEATURE_DISCOVERY: 'Insurance - Feature Discovery Grid',
  ECB_RATES: 'European Central Bank Rates',
  PLUS_CUSTOM_CATS: 'Paid Monzo - Plus - Custom Categories with Background',
  PLUS_REGULAR_MONZO:
    'Paid Monzo - Plus - Everything you love about Monzo with Background',
  BB_CTA_BANNER: 'Business Banking Link Out Banner',
  PREMIUM_TEXT_AND_IMAGE:
    'Paid Monzo - Premium - Other accounts with Background',
  PERSONAL_BANKING_TRUST_MARKER_STRIP: 'Personal banking - Trustmarks',
}

/* 3. Provide a renderer function for your section
   This function takes a Contentful API node and has to return a 
   React component wrapped in SectionWrapper (or DarkSectionWrapper) */
export const bespokeSectionMap = {
  [bespokeCodes.TEST]: renderTestSection,
  [bespokeCodes.PAID_BUNDLES]: renderPlusBundles,
  [bespokeCodes.PREMIUM_FEATURES]: renderPlusFeatures,
  [bespokeCodes.PLUS_FEATURES]: renderPlusFeatures,
  [bespokeCodes.PLUS_INSURANCE]: renderPlusInsurance,
  [bespokeCodes.EVENTS_SPEAKER_LIST]: renderEventsSpeakersList,
  [bespokeCodes.OVERDRAFT_FEE_CALC]: renderOverdraftFeeCalculator,
  [bespokeCodes.OVERDRAFT_ELIGIBLE]: renderOverdraftEligibilityChecker,
  [bespokeCodes.CARD_RENDER_CTA_PREMIUM]: renderCardRender,
  [bespokeCodes.CARD_RENDER_CTA_PLUS]: renderCardRender,
  [bespokeCodes.USA_FEATURES]: renderUSAFeatureChecklist,
  [bespokeCodes.USA_WAITLIST]: renderUSAWaitlist,
  [bespokeCodes.BUSINESS_MORE_INFORMATION_FORM]: renderBusinessMoreInformationSection,
  [bespokeCodes.BUSINESS_PRICING_TABLE]: renderBusinessPricingTable,
  [bespokeCodes.BUSINESS_CUSTOMER_QUOTE_DIPTYCH_FEATURES]: renderBusinessCustomerQuoteDiptych,
  [bespokeCodes.BUSINESS_CUSTOMER_QUOTE_DIPTYCH_TESTIMONIALS]: renderBusinessCustomerQuoteDiptych,
  [bespokeCodes.BUSINESS_CUSTOMER_PROFILE_TRIPTYCH_LANDING]: renderBusinessCustomerProfileTriptych,
  [bespokeCodes.BUSINESS_CUSTOMER_PROFILE_TRIPTYCH_TESTIMONIALS_1]: renderBusinessCustomerProfileTriptych,
  [bespokeCodes.BUSINESS_CUSTOMER_PROFILE_TRIPTYCH_TESTIMONIALS_2]: renderBusinessCustomerProfileTriptych,
  [bespokeCodes.BUSINESS_COMPARISON_TABLE]: renderBusinessComparisonTable,
  [bespokeCodes.BUSINESS_TRUST_MARKER_STRIP]: renderTrustMarkerStrip,
  [bespokeCodes.BUSINESS_FEATURE_DISCOVERY]: renderBusinessFeatureDiscoveryGrid,
  [bespokeCodes.PAID_FEATURE_DISCOVERY]: renderPaidFeatureDiscoveryGrid,
  [bespokeCodes.PAID_INSURANCE_FEATURE_DISCOVERY]: renderPaidFeatureDiscoveryGrid,
  [bespokeCodes.ECB_RATES]: renderECBRates, // ECB == European Central Bank
  [bespokeCodes.PLUS_CUSTOM_CATS]: renderCustomTextAndImageWithBackground,
  [bespokeCodes.PLUS_REGULAR_MONZO]: renderCustomTextAndImageWithBackground,
  [bespokeCodes.BB_CTA_BANNER]: renderBusinesssBankingCTABanner,
  [bespokeCodes.ALL_BUNDLES]: renderPlusBundles,
  [bespokeCodes.PREMIUM_TEXT_AND_IMAGE]: renderCustomTextAndImageWithBackground,
  [bespokeCodes.PERSONAL_BANKING_TRUST_MARKER_STRIP]: renderTrustMarkerStrip,
}
